import type { Equipment, RepBucket, UnitSystem } from '@lift2fit/shared-types';

export const KG_TO_LB = 2.20462;

/** Canonical storage unit is kilograms; pounds are a presentation concern only. */
export function kgToLb(kg: number): number {
  return kg * KG_TO_LB;
}

export function lbToKg(lb: number): number {
  return lb / KG_TO_LB;
}

/** Display rounding for imperial: lifters load in 0.5 lb steps, not 0.4536. */
export function roundLb(lb: number): number {
  return Math.round(lb * 2) / 2;
}

/** Convert a display value back to canonical kilograms, snapped to 0.5 kg. */
export function displayToKg(value: number, unit: UnitSystem): number {
  const kg = unit === 'imperial' ? lbToKg(value) : value;
  return Math.round(kg * 2) / 2;
}

export function kgToDisplay(kg: number, unit: UnitSystem): number {
  return unit === 'imperial' ? roundLb(kgToLb(kg)) : Math.round(kg * 2) / 2;
}

/** Load increment per equipment type, in the display unit. */
export function loadIncrement(equipment: Equipment, unit: UnitSystem): number {
  if (unit === 'imperial') {
    return equipment === 'barbell' || equipment === 'smith' ? 5 : 2.5;
  }
  return equipment === 'barbell' || equipment === 'smith' ? 2.5 : 1.25;
}

/** Micro-load used by long-press / shift-click on the weight stepper. */
export function microIncrement(unit: UnitSystem): number {
  return unit === 'imperial' ? 0.5 : 0.25;
}

/** Epley estimated 1RM, identical to public.epley_1rm() in SQL (2dp, reps capped at 12). */
export function epley1rm(weightKg: number, reps: number): number {
  if (!Number.isFinite(weightKg) || reps <= 0) return 0;
  const capped = Math.min(reps, 12);
  return Math.round(weightKg * (1 + capped / 30) * 100) / 100;
}

/** Identical to public.rep_bucket_for() in SQL. */
export function repBucket(reps: number): RepBucket {
  if (reps <= 3) return '1-3';
  if (reps <= 6) return '4-6';
  if (reps <= 10) return '7-10';
  return '11+';
}

/** "102.5 kg × 5 @ RPE 8" — the canonical set string used across every surface. */
export function formatSet(weightKg: number, reps: number, unit: UnitSystem, rpe?: number | null): string {
  const w = kgToDisplay(weightKg, unit);
  const wText = unit === 'imperial' ? `${w} lb` : `${w} kg`;
  const rpeText = rpe === null || rpe === undefined ? '' : ` @ RPE ${rpe}`;
  return `${wText} × ${reps}${rpeText}`;
}

export function formatVolume(volumeKg: number, unit: UnitSystem): string {
  const value = unit === 'imperial' ? kgToLb(volumeKg) : volumeKg;
  const rounded = Math.round(value);
  return `${rounded.toLocaleString('en-GB')} ${unit === 'imperial' ? 'lb' : 'kg'}`;
}

export function formatWeight(weightKg: number | null, unit: UnitSystem): string {
  if (weightKg === null) return '—';
  return `${kgToDisplay(weightKg, unit)} ${unit === 'imperial' ? 'lb' : 'kg'}`;
}
